// components/create/DateTimeSheet.js
//
// Picking a date, and picking a time, as sheets.
//
// ── What this replaces, and why not just restyle it ────────────────────────
//
// react-datepicker, with its stock `react-datepicker.css` imported and never
// overridden. That stylesheet is a light theme: a white panel, near-black
// text, `#216ba5` selection. On this dark form the result was a white slab —
// the time list had white text on white, so it read as empty, and the selected
// day turned white-on-white the moment it was tapped. Nothing was broken; it
// was simply the wrong theme, all the way down.
//
// Restyling it means overriding forty-odd `.react-datepicker__*` selectors and
// keeping up with them across upgrades, for a widget whose popper positioning
// also has to fight the `backdrop-filter` layers on this form — the same thing
// that put the event-type dropdown behind the cards. Two sheets are less code
// than that override file would be, and they match the pickers beside them.
//
// ── Why sheets rather than inline ──────────────────────────────────────────
//
// An inline dropdown inside a `.section-card` loses to the cards below it: a
// backdrop-filter promotes each card to its own composited layer, and a later
// sibling paints over an earlier sibling's positioned child whatever its
// z-index says. See EventTypeSelector.js for the same fix. Out of the flow, it
// cannot lose — on a phone or a desktop.
//
// "Out of the flow" has to mean the portal, not just `position: fixed`: a
// fixed element whose ancestor has a backdrop-filter is laid out against that
// ancestor and painted inside it, so a sheet rendered in a .section-card was
// still trapped in the card. See components/Portal.js.

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { FiX, FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import '../../styles/DateTimeSheet.scss';
import Portal from '../Portal';

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const sameDay = (a, b) =>
  !!a && !!b
  && a.getFullYear() === b.getFullYear()
  && a.getMonth() === b.getMonth()
  && a.getDate() === b.getDate();

/** Every cell of the grid: leading blanks, then the month's days. */
function monthGrid(year, month) {
  const first = new Date(year, month, 1);
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells = new Array(first.getDay()).fill(null);
  for (let day = 1; day <= daysInMonth; day += 1) cells.push(new Date(year, month, day));
  return cells;
}

function Sheet({ title, onClose, children, footer }) {
  const panelRef = useRef(null);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previous;
    };
  }, [onClose]);

  return (
    <Portal>
    <div className="dts-scrim" onClick={onClose}>
      <div
        ref={panelRef}
        className="dts-sheet"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="dts-head">
          <h2>{title}</h2>
          <button type="button" onClick={onClose} aria-label="Close"><FiX /></button>
        </div>
        <div className="dts-body">{children}</div>
        {footer && <div className="dts-foot">{footer}</div>}
      </div>
    </div>
    </Portal>
  );
}

// ── Date ───────────────────────────────────────────────────────────────────

export function DateSheet({ value, onChange, onClose, title = 'Pick a date', minDate }) {
  const initial = value || new Date();
  const [cursor, setCursor] = useState(new Date(initial.getFullYear(), initial.getMonth(), 1));

  const today = startOfDay(new Date());
  const floor = minDate ? startOfDay(minDate) : null;

  const cells = useMemo(
    () => monthGrid(cursor.getFullYear(), cursor.getMonth()),
    [cursor],
  );

  const step = (months) =>
    setCursor((c) => new Date(c.getFullYear(), c.getMonth() + months, 1));

  return (
    <Sheet title={title} onClose={onClose}>
      <div className="dts-month">
        <button type="button" onClick={() => step(-1)} aria-label="Previous month">
          <FiChevronLeft />
        </button>
        <span aria-live="polite">
          {MONTH_NAMES[cursor.getMonth()]} {cursor.getFullYear()}
        </span>
        <button type="button" onClick={() => step(1)} aria-label="Next month">
          <FiChevronRight />
        </button>
      </div>

      <div className="dts-daynames" aria-hidden="true">
        {DAY_NAMES.map((d) => <span key={d}>{d}</span>)}
      </div>

      <div className="dts-grid" role="grid">
        {cells.map((date, index) => {
          if (!date) return <span key={`blank-${index}`} className="dts-cell is-blank" />;

          const selected = sameDay(date, value);
          const isToday = sameDay(date, today);
          const disabled = floor && date < floor;

          return (
            <button
              key={date.toISOString()}
              type="button"
              role="gridcell"
              className={`dts-cell ${selected ? 'is-selected' : ''} ${isToday ? 'is-today' : ''}`}
              aria-selected={selected}
              aria-label={date.toDateString()}
              disabled={disabled}
              onClick={() => { onChange(date); onClose(); }}
            >
              {date.getDate()}
            </button>
          );
        })}
      </div>
    </Sheet>
  );
}

// ── Time ───────────────────────────────────────────────────────────────────

/**
 * Every slot in the day, at `interval` minutes.
 *
 * Built from a date rather than a clock so the value carries a real Date —
 * which is what the create form's save path expects, since it reads
 * `.getHours()` off it.
 */
function timeSlots(interval) {
  const slots = [];
  for (let minutes = 0; minutes < 24 * 60; minutes += interval) {
    const d = new Date(2000, 0, 1, Math.floor(minutes / 60), minutes % 60);
    slots.push(d);
  }
  return slots;
}

export function TimeSheet({ value, onChange, onClose, title = 'Pick a time', interval = 15 }) {
  const slots = useMemo(() => timeSlots(interval), [interval]);
  const listRef = useRef(null);

  const selectedKey = value
    ? `${value.getHours()}:${value.getMinutes()}`
    : null;

  // Open on the chosen time rather than at midnight, so an evening party is
  // not eighty rows of scrolling away.
  useEffect(() => {
    const el = listRef.current?.querySelector('.is-selected');
    // Guarded: scrollIntoView is missing in jsdom and in a few older engines,
    // and an exception thrown from an effect unmounts the whole sheet — so the
    // picker would vanish rather than merely open at the wrong scroll offset.
    if (el && typeof el.scrollIntoView === 'function') {
      el.scrollIntoView({ block: 'center' });
    }
  }, []);

  const pick = (slot) => {
    // A fresh Date on today's date carrying the chosen clock time: the form
    // combines it with the start date at save, and only the hours and minutes
    // are read.
    const now = new Date();
    onChange(new Date(
      now.getFullYear(), now.getMonth(), now.getDate(),
      slot.getHours(), slot.getMinutes(), 0, 0,
    ));
    onClose();
  };

  return (
    <Sheet title={title} onClose={onClose}>
      <div className="dts-times" ref={listRef} role="listbox" aria-label={title}>
        {slots.map((slot) => {
          const key = `${slot.getHours()}:${slot.getMinutes()}`;
          const selected = key === selectedKey;
          return (
            <button
              key={key}
              type="button"
              role="option"
              aria-selected={selected}
              className={`dts-time ${selected ? 'is-selected' : ''}`}
              onClick={() => pick(slot)}
            >
              {/* 'en-US' explicitly, matching the field that shows the chosen
                  time in BasicSection. With the default locale the two
                  disagreed on case — the list said "7:00 pm" and the field
                  said "7:00 PM" — which looks like two different times. */}
              {slot.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
            </button>
          );
        })}
      </div>
    </Sheet>
  );
}

// ── The button that opens them ─────────────────────────────────────────────

export function DateTimeField({ label, value, placeholder, onOpen, invalid, format }) {
  return (
    <button
      type="button"
      className={`dts-field ${invalid ? 'is-invalid' : ''} ${value ? '' : 'is-empty'}`}
      onClick={onOpen}
      aria-label={label}
    >
      {value ? format(value) : placeholder}
    </button>
  );
}
